import React, { createContext, useMemo, type FC, type PropsWithChildren } from "react";
import { GQL } from "@binale-tech/shared";
import type { GenericRecord } from "@domain/models/GenericRecord";
import { type PaymentPrototype } from "@domain/models/Payment";
import { preprocessPriorities } from "@domain/context/accountingData/utils/recordControlUtils";
import { type ImportManyOptions, useRecordsSaver } from "@domain/context/accountingData/hooks/useRecordsSaver";

export interface RecordActions {
    save: (v: GenericRecord, payment?: PaymentPrototype, opts?: { aiSuggestion?: string }) => Promise<GQL.ISaveSummary>;
    saveTemplate: (v: GenericRecord) => Promise<GQL.ISaveSummary>;
    saveDocuments: (id: string, documents: GQL.IEntityDocumentId[]) => Promise<GQL.ISaveSummary>;
    importMany: (v: GenericRecord[], opts?: ImportManyOptions) => Promise<any>;
    confirm: (v: string[]) => Promise<void>;
    journal: (v: string[]) => Promise<void>;
    color: (v: string[], color: GQL.IDocumentColors | null) => Promise<void>;
    deleteByKeys: (v: string[]) => Promise<GQL.ISaveSummary>;
    deleteTemplates: (v: string[]) => Promise<GQL.ISaveSummary>;
    cancelByKeys: (v: string[]) => Promise<GQL.ISaveSummary>;
    // methods to make unified signature for all modules
    sinkFirstDown: (v: GenericRecord[]) => Promise<void>;
    swimLastUp: (v: GenericRecord[]) => Promise<void>;
}
const blackholeActions: RecordActions = {
    save: () => Promise.resolve({ status: true, summary: [] }),
    saveTemplate: () => Promise.resolve({ status: true, summary: [] }),
    saveDocuments: () => Promise.resolve({ status: true, summary: [] }),
    deleteByKeys: () => Promise.resolve({ status: true, summary: [] }),
    deleteTemplates: () => Promise.resolve({ status: true, summary: [] }),
    cancelByKeys: () => Promise.resolve({ status: true, summary: [] }),
    importMany: () => Promise.resolve(),
    confirm: () => Promise.resolve(),
    journal: () => Promise.resolve(),
    color: () => Promise.resolve(),
    sinkFirstDown: () => Promise.resolve(),
    swimLastUp: () => Promise.resolve(),
};

interface GroupedRecordActions {
    save: (v: GenericRecord, payment: PaymentPrototype, groupId: string) => Promise<GQL.ISaveSummary>;
    saveTemplate: (v: GenericRecord, groupId: string) => Promise<GQL.ISaveSummary>;
    saveDocuments: (id: string, documents: GQL.IEntityDocumentId[]) => Promise<GQL.ISaveSummary>;
    deleteByKeys: (v: string[]) => Promise<GQL.ISaveSummary>;
    deleteTemplates: (v: string[]) => Promise<GQL.ISaveSummary>;
    cancelByKeys: (v: string[]) => Promise<GQL.ISaveSummary>;
    importMany: (v: GenericRecord[], groupId: string, opts?: ImportManyOptions) => Promise<void>;
    confirm: (v: string[]) => Promise<void>;
    journal: (v: string[]) => Promise<void>;
    color: (v: string[], color: GQL.IDocumentColors | null) => Promise<void>;
    sinkFirstDown: (v: GenericRecord[], groupId: string) => Promise<void>;
    swimLastUp: (v: GenericRecord[], groupId: string) => Promise<void>;
}

export type RecordsController = {
    actionsER: RecordActions;
    actionsAZ: RecordActions;
    actionsFE: RecordActions;
    actionsPOS: RecordActions;
    actionsLA: RecordActions;
    actionsDeb: RecordActions;
    actionsBank: GroupedRecordActions;
    actionsKB: GroupedRecordActions;

    blackhole: RecordActions;
};

export const convertGroupActions = (groupActions: GroupedRecordActions, groupId: string): RecordActions => ({
    save: (v, payment) => groupActions.save(v, payment, groupId),
    saveTemplate: (v) => groupActions.saveTemplate(v, groupId),
    saveDocuments: (id, docs) => groupActions.saveDocuments(id, docs),
    importMany: (v, opts) => groupActions.importMany(v, groupId, opts),
    deleteByKeys: (v) => groupActions.deleteByKeys(v),
    deleteTemplates: (v) => groupActions.deleteTemplates(v),
    cancelByKeys: (v) => groupActions.cancelByKeys(v),
    confirm: (v) => groupActions.confirm(v),
    journal: (v) => groupActions.journal(v),
    color: (v, color) => groupActions.color(v, color),
    sinkFirstDown: (v) => groupActions.sinkFirstDown(v, groupId),
    swimLastUp: (v) => groupActions.swimLastUp(v, groupId),
});

const defaultActions: RecordActions = {
    ...blackholeActions,
    save: () => Promise.reject(),
    importMany: () => Promise.reject(),
    deleteByKeys: () => Promise.reject(),
};

const defaultGroupedActions: GroupedRecordActions = {
    save: (_v: GenericRecord, _payment: PaymentPrototype, _groupId: string) => Promise.reject(),
    saveTemplate: (_v: GenericRecord, _groupId: string) => Promise.reject(),
    saveDocuments: (_id: string, _docs: GQL.IEntityDocumentId[]) => Promise.reject(),
    importMany: (_v: GenericRecord[], _groupId: string) => Promise.reject(),
    deleteByKeys: (_v: string[]) => Promise.reject(),
    deleteTemplates: (_v: string[]) => Promise.reject(),
    cancelByKeys: (_v: string[]) => Promise.reject(),
    confirm: (_v: string[]) => Promise.reject(),
    journal: (_v: string[]) => Promise.reject(),
    color: (_v: string[], _color: GQL.IDocumentColors | null) => Promise.reject(),
    sinkFirstDown: (_v: GenericRecord[], _groupId: string) => Promise.reject("not implemented"),
    swimLastUp: (_v: GenericRecord[], _groupId: string) => Promise.reject("not implemented"),
};

export const RecordsControlContext = createContext<RecordsController>({
    actionsER: defaultActions,
    actionsAZ: defaultActions,
    actionsFE: defaultActions,
    actionsPOS: defaultActions,
    actionsLA: defaultActions,
    actionsDeb: defaultActions,
    actionsKB: defaultGroupedActions,
    actionsBank: defaultGroupedActions,
    blackhole: defaultActions,
});

const getController = (recordSaver: ReturnType<typeof useRecordsSaver>): RecordsController => {
    return {
        actionsER: {
            ...blackholeActions,
            save: (record, _payment, opts) =>
                recordSaver.save(record, null, GQL.IProductKey.Er, { aiSuggestion: opts?.aiSuggestion }),
            saveTemplate: (record) => recordSaver.save(record, null, GQL.IProductKey.Er, { isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.Er, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Er),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Er, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.Er),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.Er),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.Er),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.Er, color),
            importMany: (records: GenericRecord[], opts?: ImportManyOptions) =>
                recordSaver.importMany(records, GQL.IProductKey.Er, undefined, opts),
        },
        actionsAZ: {
            ...blackholeActions,
            save: (record) => recordSaver.save(record, null, GQL.IProductKey.ErA),
            saveTemplate: (record) => recordSaver.save(record, null, GQL.IProductKey.ErA, { isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.ErA, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.ErA),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.ErA, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.ErA),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.ErA),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.ErA),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.ErA, color),

            importMany: (records, opts) => recordSaver.importMany(records, GQL.IProductKey.ErA, undefined, opts),
        },
        actionsFE: {
            ...blackholeActions,
            save: (record, payment) => recordSaver.save(record, payment, GQL.IProductKey.Fe),
            saveTemplate: (record) => recordSaver.save(record, null, GQL.IProductKey.Fe, { isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.Fe, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Fe),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Fe, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.Fe),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.Fe),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.Fe),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.Fe, color),

            importMany: (records, opts) => recordSaver.importMany(records, GQL.IProductKey.Fe, undefined, opts),
        },
        actionsPOS: {
            ...blackholeActions,
            save: (record) => recordSaver.save(record, null, GQL.IProductKey.Pos),
            saveTemplate: (record) => recordSaver.save(record, null, GQL.IProductKey.Pos, { isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.Pos, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Pos),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Pos, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.Pos),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.Pos),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.Pos),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.Pos, color),

            importMany: (records, opts) => recordSaver.importMany(records, GQL.IProductKey.Pos, undefined, opts),
        },
        actionsLA: {
            ...blackholeActions,
            save: (record) => recordSaver.save(record, null, GQL.IProductKey.La),
            saveTemplate: (record) => recordSaver.save(record, null, GQL.IProductKey.La, { isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.La, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.La),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.La, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.La),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.La),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.La),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.La, color),

            importMany: (records, opts) => recordSaver.importMany(records, GQL.IProductKey.La, undefined, opts),
        },
        actionsDeb: {
            ...blackholeActions,
            save: (record) => recordSaver.save(record, null, GQL.IProductKey.Deb),
            saveTemplate: (record) => recordSaver.save(record, null, GQL.IProductKey.Deb, { isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.Deb, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Deb),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Deb, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.Deb),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.Deb),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.Deb),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.Deb, color),

            importMany: (records, opts) => recordSaver.importMany(records, GQL.IProductKey.Deb, undefined, opts),
        },
        actionsKB: {
            save: (record, payment, groupId) => recordSaver.save(record, payment, GQL.IProductKey.Kb, { groupId }),
            saveTemplate: (record, groupId) =>
                recordSaver.save(record, null, GQL.IProductKey.Kb, { groupId, isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.Kb, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Kb),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Kb, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.Kb),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.Kb),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.Kb),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.Kb, color),
            importMany: (records, groupId, opts) => recordSaver.importMany(records, GQL.IProductKey.Kb, groupId, opts),
            swimLastUp: (vs, groupId) => {
                if (vs.length < 2) {
                    return Promise.resolve();
                }
                const prioList = preprocessPriorities(vs);

                const topPrio = prioList[0].priority;
                const downPrio = prioList[prioList.length - 1].priority;
                prioList.slice(0, prioList.length - 1).reduceRight((prio, v) => {
                    const _prio = v.priority;
                    v.priority = prio;
                    return _prio;
                }, downPrio);
                prioList[prioList.length - 1].priority = topPrio;
                const updates: Record<string, number> = {};
                prioList.forEach((r) => (updates[r.key] = r.priority));
                return recordSaver.setPriorities(updates, GQL.IProductKey.Kb, groupId);
            },
            sinkFirstDown: (vs, groupId) => {
                if (vs.length < 2) {
                    return Promise.resolve();
                }
                const prioList = preprocessPriorities(vs);
                const topPrio = prioList[0].priority;
                const downPrio = prioList[prioList.length - 1].priority;
                prioList.slice(1).reduce((prio, v) => {
                    const _prio = v.priority;
                    v.priority = prio;
                    return _prio;
                }, topPrio);
                prioList[0].priority = downPrio;
                const updates: Record<string, number> = {};
                prioList.forEach((r) => (updates[r.key] = r.priority));
                return recordSaver.setPriorities(updates, GQL.IProductKey.Kb, groupId);
            },
        },
        actionsBank: {
            save: (record, payment, groupId) => recordSaver.save(record, payment, GQL.IProductKey.Bank, { groupId }),
            saveTemplate: (record, groupId) =>
                recordSaver.save(record, null, GQL.IProductKey.Bank, { groupId, isTemplate: true }),
            saveDocuments: (id, docs) => recordSaver.saveDocuments(id, GQL.IProductKey.Bank, docs),
            deleteByKeys: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Bank),
            deleteTemplates: (ids) => recordSaver.deleteByKeys(ids, GQL.IProductKey.Bank, { isTemplate: true }),
            cancelByKeys: (ids) => recordSaver.cancelByKeys(ids, GQL.IProductKey.Bank),
            confirm: (ids) => recordSaver.confirm(ids, GQL.IProductKey.Bank),
            journal: (ids) => recordSaver.journal(ids, GQL.IProductKey.Bank),
            color: (ids, color) => recordSaver.color(ids, GQL.IProductKey.Bank, color),
            importMany: (records, groupId, opts) =>
                recordSaver.importMany(records, GQL.IProductKey.Bank, groupId, opts),
            swimLastUp: () => Promise.reject("not implemented"),
            sinkFirstDown: () => Promise.reject("not implemented"),
        },
        blackhole: { ...blackholeActions },
    };
};

export const RecordsControlProvider: FC<PropsWithChildren> = (props) => {
    const recordSaver = useRecordsSaver();

    const control: RecordsController = useMemo(() => {
        return getController(recordSaver);
    }, [recordSaver]);

    return <RecordsControlContext value={control}>{props.children}</RecordsControlContext>;
};
